import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import AdminHeader from '../../../components/feature/AdminHeader';

interface MissionField {
  id: string;
  number: string;
  location: string;
  title: string;
  icon: string;
  image_url: string;
  video_url: string | null;
  prayer: string;
  details: string[];
  display_order: number;
  created_at: string;
}

export default function AdminMissionFields() {
  const [fields, setFields] = useState<MissionField[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    number: '',
    location: '',
    title: '',
    icon: 'ri-map-pin-line',
    image_url: '',
    video_url: '',
    prayer: '',
    details: [''],
    display_order: 0
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [contentType, setContentType] = useState<'prayer' | 'details'>('prayer');

  const iconOptions = [
    { value: 'ri-graduation-cap-line', label: '교육/장학' },
    { value: 'ri-building-line', label: '건물/센터' },
    { value: 'ri-book-open-line', label: '책/학습' },
    { value: 'ri-community-line', label: '커뮤니티' },
    { value: 'ri-team-line', label: '팀/그룹' },
    { value: 'ri-heart-line', label: '사랑/봉사' },
    { value: 'ri-global-line', label: '글로벌' },
    { value: 'ri-map-pin-line', label: '위치' },
    { value: 'ri-church-line', label: '교회' },
    { value: 'ri-hand-heart-line', label: '기도' }
  ];

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const { data, error } = await supabase
        .from('mission_fields')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error) {
      console.error('Error fetching fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `mission-fields/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setFormData({ ...formData, image_url: publicUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('이미지 업로드 실패');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 크기 체크 (100MB 제한)
    if (file.size > 100 * 1024 * 1024) {
      alert('동영상 파일은 100MB 이하만 업로드 가능합니다.');
      return;
    }

    setUploadingVideo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `mission-fields/videos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setFormData({ ...formData, video_url: publicUrl });
      alert('동영상 업로드 완료!');
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('동영상 업로드 실패');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleRemoveVideo = () => {
    setFormData({ ...formData, video_url: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dataToSave = {
      ...formData,
      video_url: formData.video_url || null,
      prayer: contentType === 'prayer' ? formData.prayer : null,
      details: contentType === 'details' ? formData.details.filter(d => d.trim()) : null,
      updated_at: new Date().toISOString()
    };

    try {
      if (editingId) {
        const { error } = await supabase
          .from('mission_fields')
          .update(dataToSave)
          .eq('id', editingId);

        if (error) throw error;
        alert('수정되었습니다');
      } else {
        const { error } = await supabase
          .from('mission_fields')
          .insert([dataToSave]);

        if (error) throw error;
        alert('추가되었습니다');
      }

      setFormData({
        number: '',
        location: '',
        title: '',
        icon: 'ri-map-pin-line',
        image_url: '',
        video_url: '',
        prayer: '',
        details: [''],
        display_order: 0
      });
      setEditingId(null);
      setContentType('prayer');
      fetchFields();
    } catch (error) {
      console.error('Error saving field:', error);
      alert('저장 실패');
    }
  };

  const handleEdit = (field: MissionField) => {
    setFormData({
      number: field.number,
      location: field.location,
      title: field.title,
      icon: field.icon,
      image_url: field.image_url,
      video_url: field.video_url || '',
      prayer: field.prayer || '',
      details: field.details || [''],
      display_order: field.display_order
    });
    setContentType(field.prayer ? 'prayer' : 'details');
    setEditingId(field.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('mission_fields')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('삭제되었습니다');
      fetchFields();
    } catch (error) {
      console.error('Error deleting field:', error);
      alert('삭제 실패');
    }
  };

  const handleCancel = () => {
    setFormData({
      number: '',
      location: '',
      title: '',
      icon: 'ri-map-pin-line',
      image_url: '',
      video_url: '',
      prayer: '',
      details: [''],
      display_order: 0
    });
    setEditingId(null);
    setContentType('prayer');
  };

  const addDetailField = () => {
    setFormData({ ...formData, details: [...formData.details, ''] });
  };

  const removeDetailField = (index: number) => {
    const newDetails = formData.details.filter((_, i) => i !== index);
    setFormData({ ...formData, details: newDetails });
  };

  const updateDetailField = (index: number, value: string) => {
    const newDetails = [...formData.details];
    newDetails[index] = value;
    setFormData({ ...formData, details: newDetails });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="후원 사역 현장 관리" subtitle="사역 현장 정보를 관리합니다" />

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-12">
        {/* Form */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">
            {editingId ? '사역 현장 수정' : '새 사역 현장 추가'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">번호 *</label>
                <input
                  type="text"
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="예: 01, 02"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">지역 *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="예: 필리핀 세부"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">제목 *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="예: MSDC 장학사역"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">아이콘 *</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
              >
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <span>미리보기:</span>
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className={`${formData.icon} text-xl text-teal-600`}></i>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">대표 이미지 *</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg text-sm"
                disabled={uploading}
              />
              {uploading && <p className="text-sm text-gray-500 mt-2">업로드 중...</p>}
              {formData.image_url && (
                <img
                  src={formData.image_url}
                  alt="미리보기"
                  className="mt-4 w-full max-w-xs h-40 object-cover rounded-lg"
                />
              )}
            </div>

            {/* 동영상 업로드 섹션 */}
            <div>
              <label className="block text-sm font-medium mb-2">
                동영상 (선택사항)
                <span className="text-gray-500 font-normal ml-2 text-xs">최대 100MB</span>
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg text-sm"
                disabled={uploadingVideo}
              />
              {uploadingVideo && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-teal-600">동영상 업로드 중...</p>
                </div>
              )}
              {formData.video_url && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-green-600 font-medium flex items-center gap-2">
                      <i className="ri-check-line"></i>
                      동영상 업로드됨
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveVideo}
                      className="text-sm text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap"
                    >
                      삭제
                    </button>
                  </div>
                  <video
                    src={formData.video_url}
                    controls
                    playsInline
                    className="w-full max-w-xs rounded-lg"
                  >
                    브라우저가 동영상을 지원하지 않습니다.
                  </video>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">콘텐츠 유형 *</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="prayer"
                    checked={contentType === 'prayer'}
                    onChange={(e) => setContentType(e.target.value as 'prayer' | 'details')}
                    className="w-4 h-4 text-teal-600"
                  />
                  <span className="text-sm">기도 제목</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="details"
                    checked={contentType === 'details'}
                    onChange={(e) => setContentType(e.target.value as 'prayer' | 'details')}
                    className="w-4 h-4 text-teal-600"
                  />
                  <span className="text-sm">세부 사역 내용</span>
                </label>
              </div>
            </div>

            {contentType === 'prayer' ? (
              <div>
                <label className="block text-sm font-medium mb-2">기도 제목</label>
                <textarea
                  value={formData.prayer}
                  onChange={(e) => setFormData({ ...formData, prayer: e.target.value })}
                  rows={4}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="기도 제목을 입력하세요"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">세부 사역 내용</label>
                <div className="space-y-2">
                  {formData.details.map((detail, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={detail}
                        onChange={(e) => updateDetailField(index, e.target.value)}
                        className="flex-1 px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder={`사역 내용 ${index + 1}`}
                      />
                      {formData.details.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDetailField(index)}
                          className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap text-sm cursor-pointer"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addDetailField}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap text-sm cursor-pointer"
                  >
                    + 추가
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">표시 순서</label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                {editingId ? '수정하기' : '추가하기'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">사역 현장 목록</h2>
          {fields.length === 0 ? (
            <p className="text-gray-500 text-center py-8">등록된 사역 현장이 없습니다</p>
          ) : (
            <div className="space-y-4">
              {fields.map((field) => (
                <div
                  key={field.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-full sm:w-32 flex-shrink-0">
                      <img
                        src={field.image_url}
                        alt={field.title}
                        className="w-full sm:w-32 h-40 sm:h-20 object-cover rounded-lg"
                      />
                      {field.video_url && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-teal-600">
                          <i className="ri-video-line"></i>
                          <span>동영상 있음</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-bold rounded-full">
                              {field.number}
                            </span>
                            <span className="text-sm text-teal-600 font-semibold">
                              {field.location}
                            </span>
                          </div>
                          <h3 className="font-semibold text-base sm:text-lg mb-2">{field.title}</h3>
                          {field.prayer && (
                            <p className="text-sm text-gray-600 line-clamp-2">{field.prayer}</p>
                          )}
                          {field.details && (
                            <ul className="text-sm text-gray-600 space-y-1">
                              {field.details.slice(0, 2).map((detail, idx) => (
                                <li key={idx}>• {detail}</li>
                              ))}
                              {field.details.length > 2 && (
                                <li className="text-gray-400">외 {field.details.length - 2}개...</li>
                              )}
                            </ul>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            순서: {field.display_order}
                          </p>
                        </div>
                        <div className="flex gap-2 self-end sm:self-start">
                          <button
                            onClick={() => handleEdit(field)}
                            className="flex-1 sm:flex-none px-4 py-2 bg-teal-500 text-white text-sm rounded hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            수정
                          </button>
                          <button
                            onClick={() => handleDelete(field.id)}
                            className="flex-1 sm:flex-none px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
